import React, { useState, useEffect, useCallback } from 'react';
import PageWrapper from '../components/PageWrapper';
import {
  ShieldCheckIcon,
  KeyIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { getRoles, getPermissions, addPermissionToRole, removePermissionFromRole } from '../services/adminService';

// ─── Category label map ────────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  users: 'Users',
  agencies: 'Agencies',
  properties: 'Properties',
  rooms: 'Rooms',
  leases: 'Leases',
  payments: 'Payments',
  expenses: 'Expenses',
  ledgers: 'Ledgers',
  payouts: 'Payouts',
  reports: 'Reports',
  roles: 'Roles & Permissions',
  system: 'System',
};

// Group permissions by category
const groupByCategory = (permissions) => {
  return permissions.reduce((acc, p) => {
    const cat = p.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
};

// ─── Manage Permissions Modal ──────────────────────────────────────────────────
const ManagePermissionsModal = ({ role, allPermissions, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rolePermIds = new Set((role.permissions || []).map((p) => p.id));
  const grouped = groupByCategory(allPermissions);

  const toggle = async (permission) => {
    setLoading(true);
    setError('');
    try {
      if (rolePermIds.has(permission.id)) {
        await removePermissionFromRole(role.id, permission.id);
      } else {
        await addPermissionToRole(role.id, permission.id);
      }
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-6 w-6 text-emerald-600 mr-2" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {role.display_name || role.name}
              </h2>
              <p className="text-sm text-gray-500">
                {role.description || 'Manage permissions for this role'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {loading && (
            <div className="mb-3 flex items-center text-sm text-emerald-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2" />
              Saving…
            </div>
          )}

          <div className="space-y-6">
            {Object.entries(grouped).map(([category, perms]) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {CATEGORY_LABELS[category] || category}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {perms.map((perm) => {
                    const hasIt = rolePermIds.has(perm.id);
                    return (
                      <button
                        key={perm.id}
                        onClick={() => toggle(perm)}
                        disabled={loading}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-colors disabled:opacity-50 ${
                          hasIt
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium">{perm.name}</p>
                          <p className="text-xs text-gray-400">{perm.code}</p>
                        </div>
                        <div
                          className={`flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center ml-3 ${
                            hasIt
                              ? 'border-emerald-500 bg-emerald-500'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {hasIt && <CheckIcon className="h-3 w-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {rolePermIds.size} of {allPermissions.length} permissions granted
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesData, permsData] = await Promise.all([getRoles(), getPermissions()]);
      setRoles(rolesData || []);
      setAllPermissions(permsData || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // After toggling a permission, refresh roles list and update modal state
  const handlePermChanged = async () => {
    const updated = await getRoles();
    setRoles(updated || []);
    if (selectedRole) {
      const refreshed = updated.find((r) => r.id === selectedRole.id);
      if (refreshed) setSelectedRole(refreshed);
    }
  };

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-8 w-8 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold">Role Management</h1>
                  <p className="text-emerald-100 text-sm mt-1">
                    Manage roles and their permission assignments
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
                </div>
              ) : roles.length === 0 ? (
                <p className="text-center text-gray-400 py-12">No roles found.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {roles.map((role) => (
                    <div
                      key={role.id}
                      className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {role.display_name || role.name}
                          </h3>
                          <p className="text-xs text-gray-400 font-mono mt-0.5">{role.name}</p>
                        </div>
                        {role.is_system && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">
                            System
                          </span>
                        )}
                      </div>

                      {role.description && (
                        <p className="text-sm text-gray-500 mb-3">{role.description}</p>
                      )}

                      {/* Permission summary */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">
                          <KeyIcon className="h-3.5 w-3.5 inline mr-1" />
                          {(role.permissions || []).length} permission
                          {(role.permissions || []).length !== 1 ? 's' : ''}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(role.permissions || []).slice(0, 4).map((p) => (
                            <span
                              key={p.id}
                              className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono"
                            >
                              {p.code}
                            </span>
                          ))}
                          {(role.permissions || []).length > 4 && (
                            <span className="text-xs text-gray-400">
                              +{(role.permissions || []).length - 4} more
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedRole(role)}
                        className="w-full py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Manage Permissions
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Permission management modal */}
      {selectedRole && (
        <ManagePermissionsModal
          role={selectedRole}
          allPermissions={allPermissions}
          onClose={() => setSelectedRole(null)}
          onChanged={handlePermChanged}
        />
      )}
    </PageWrapper>
  );
};

export default RoleManagement;

